import { useEffect, useState } from 'react';
import { Radio, ExternalLink } from 'lucide-react';
import { resolveApiUrl } from '@/providers/api-url';

interface LivestreamSourceInfo {
  id: number;
  status: string;
  recordingFileId: number;
  endedAt: string | null;
}

interface Props {
  moduleItemId: number | null;
}

/**
 * Показывает бэдж «Видео урока — запись трансляции #N» если для этого
 * урока в БД есть Livestream с linkedModuleItemId=item.id и recordingFileId.
 *
 * Источник данных — GET /api/admin/livestreams/source-livestream/:moduleItemId.
 * Эндпойнт возвращает null, если связи нет; компонент в этом случае ничего не рендерит.
 */
export function LivestreamSourceBadge({ moduleItemId }: Props) {
  const [info, setInfo] = useState<LivestreamSourceInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!moduleItemId) {
      setInfo(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void fetch(`${resolveApiUrl()}/admin/livestreams/source-livestream/${moduleItemId}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json: { data?: LivestreamSourceInfo | null } | null) => {
        if (!cancelled) {
          setInfo(json?.data ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInfo(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [moduleItemId]);

  if (loading || !info) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
      <div className="flex items-center gap-2">
        <Radio className="h-3.5 w-3.5 shrink-0" />
        <span>
          Источник видео — запись трансляции <strong>#{info.id}</strong>
          {info.endedAt && (
            <span className="ml-1 text-amber-700/80 dark:text-amber-300/70">
              ({new Date(info.endedAt).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })})
            </span>
          )}
        </span>
      </div>
      <a
        href={`/livestreams/show/${info.id}`}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1 underline-offset-2 hover:underline"
      >
        Открыть
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
